import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Snackbar,
  FormControlLabel,
  Switch
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { da } from "date-fns/locale";
import { useDropzone } from "react-dropzone";
import { useParams, useNavigate } from "react-router-dom";
import { useApi } from "../api/useApi";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToParentElement } from '@dnd-kit/modifiers';

const EditListingPage = () => {
  const { id } = useParams();
  const { authFetch } = useApi();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [carId, setCarId] = useState(0)
  const [vin, setVin] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [carType, setCarType] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [adminId, setAdminId] = useState(0)
  const [firstRegDate, setFirstRegDate] = useState(null);
  const [showTechFields, setShowTechFields] = useState(false);
  const [showInternalNotes, setShowInternalNotes] = useState(false);
  const [images, setImages] = useState([]);
  const [selectOptions, setSelectOptions] = useState({ makes: [], models: [], carTypes: [], fuelTypes: [], admins: [] });
  const [formValues, setFormValues] = useState({
    variant: "",
    registrationNumber: "",
    color: "",
    price: "",
    seats: "",
    doors: "",
    hp: "",
    km: "",
    description: "",
    internalNotes: "",
    externalVisible: false,
    onHold: false,
    vatReclaimable: false
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [listingRes, makesRes, fuelTypesRes, carTypesRes, adminsRes] = await Promise.all([
          authFetch(`/admin/listing/${id}`),
          authFetch(`/admin/listing/make`),
          authFetch(`/admin/listing/fueltype`),
          authFetch(`/admin/listing/cartype`),
          authFetch(`/admin/user`)
        ]);
        const listing = await listingRes.json();
        const makes = await makesRes.json();
        const fuelTypes = await fuelTypesRes.json();
        const carTypes = await carTypesRes.json();
        const admins = await adminsRes.json();

        // Populate form
        const car = listing.car;

        const models = await (await authFetch(`/admin/listing/model/${car.makeId}`)).json()

        setCarId(car.id || "")
        setVin(car.vin || "");
        setMake(car.makeId || "");
        setModel(car.modelId || "");
        setCarType(car.carType || "");
        setFuelType(car.fuelType || "");
        setAdminId(listing.primaryAdmin.id)
        setFirstRegDate(car.firstRegistrationDate ? new Date(car.firstRegistrationDate) : null);
        setFormValues({
          variant: car.variant || "",
          registrationNumber: car.registrationNumber || "",
          color: car.color || "",
          price: listing.price || "",
          seats: car.seats?.toString() || "",
          doors: car.doors?.toString() || "",
          hp: car.hp?.toString() || "",
          km: car.km?.toString() || "",
          description: listing.description || "",
          internalNotes: listing.notes || "",
          onHold: listing.onHold || false,
          externalVisible: listing.externalVisible || false,
          vatReclaimable: listing.car.vatReclaimable || false
        });

        setImages(
          (car.images ?? [])
            .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
            .map(img => ({
              id: String(img.id),       // use DB id for existing images
              preview: img.path,
              uploaded: true
            }))
        );

        setSelectOptions({
          makes: makes,
          models: models,
          carTypes,
          fuelTypes,
          admins
        });
      } catch (err) {
        console.error(err);
        showSnackbar('Failed to load listing', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, authFetch]);

  const showSnackbar = (msg, sev = 'success') => setSnackbar({ open: true, message: msg, severity: sev });
  const handleSnackbarClose = () => setSnackbar(prev => ({ ...prev, open: false }));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormValues((prev) => ({ ...prev, [name]: fieldValue }));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: acceptedFiles => {
      const files = acceptedFiles.map(f => ({
        id: crypto.randomUUID(),
        file: f,
        preview: URL.createObjectURL(f),
        uploaded: false,
        hasObjectUrl: true,
      }));
      setImages(prev => [...prev, ...files]);
    },
    accept: { 'image/*': [] }
  });

  const deleteImage = async img => {
    if (img.uploaded && img.id) {
      try {
        await authFetch(`/admin/listing/image/${img.id}`, { method: 'DELETE' });
      } catch (err) { console.error(err); showSnackbar('Failed to delete image', 'error'); return; }
    }
    setImages(prev => prev.filter(i => i !== img));
  };

  const handleSaveListing = async () => {
    const formData = new FormData();
    formData.append('id', id);
    formData.append('carId', carId);
    formData.append('vin', vin);
    formData.append('make', make);
    formData.append('model', model);
    formData.append('carType', carType);
    formData.append('fuelType', fuelType);
    formData.append('adminId', adminId)
    formData.append('firstRegistrationDate', firstRegDate?.toISOString().split('T')[0] || '');
    formData.append('registrationNumber', formValues.registrationNumber);
    formData.append('variant', formValues.variant);
    formData.append('color', formValues.color);
    formData.append('price', formValues.price)
    formData.append('description', formValues.description);
    formData.append('internalNotes', formValues.internalNotes);
    formData.append('externalVisible', formValues.externalVisible);
    formData.append('onHold', formValues.onHold);
    formData.append('vatReclaimable', formValues.vatReclaimable)

    const parseIntOrNull = (val) => {
      if (val === "" || val === null || isNaN(Number(val))) return "";
      return parseInt(val, 10);
    };

    formData.append('seats', parseIntOrNull(formValues.seats));
    formData.append('doors', parseIntOrNull(formValues.doors));
    formData.append('hp', parseIntOrNull(formValues.hp));
    formData.append('km', parseIntOrNull(formValues.km));

    // 1) New files only, keyed by client id
    images.forEach(img => {
      if (!img.uploaded && img.file instanceof File) {
        formData.append(`images[${img.id}]`, img.file, img.file.name);
      }
    });

    // 2) Order for ALL images (existing + new) as bracketed fields -> Map<String,Integer> on server
    images.forEach((img, idx) => {
      formData.append(`imagesOrder[${img.id}]`, String(idx)); // FormData must be strings
    });

    try {
      setLoading(true);
      const res = await authFetch(`/admin/listing/update`, {
        method: 'PUT', body: formData
      });
      if (!res.ok) throw new Error(await res.text());
      showSnackbar('Listing updated successfully!', 'success');
      setTimeout(() => navigate('/admin/listings'), 1000);
    } catch (err) {
      console.error(err);
      showSnackbar('Failed to update listing', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setImages(prev => {
      const oldIndex = prev.findIndex(i => i.id === active.id);
      const newIndex = prev.findIndex(i => i.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  function SortableThumb({ item, index, onRemove }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      cursor: 'grab',
      opacity: isDragging ? 0.8 : 1,
    };

    return (
      <Grid item xs={4} sm={3} md={2} ref={setNodeRef} style={style}>
        <Box sx={{ position: 'relative' }} {...attributes} {...listeners}>
          <Box
            component="img"
            src={item.preview}
            alt={`preview-${index}`}
            sx={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 1, boxShadow: 1, userSelect: 'none' }}
            draggable={false}
          />
          <IconButton
            onClick={() => {
              if (item.hasObjectUrl) URL.revokeObjectURL(item.preview);
              onRemove();
            }}
            size="small"
            sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Grid>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={da}>
      <Box maxWidth="md" mx="auto" mt={4}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" mb={2}>Edit Listing</Typography>

          <Box mb={2}>
            <Box {...getRootProps()} sx={{ border: '2px dashed', p: 4, textAlign: 'center', bgcolor: isDragActive ? 'grey.100' : 'transparent' }}>
              <input {...getInputProps()} />
              <UploadFileIcon sx={{ fontSize: 40, color: 'grey.600', mb: 1 }} />
              <Typography>{isDragActive ? 'Drop images...' : 'Drag & drop images, or click to select'}</Typography>
            </Box>
            {images.length > 0 && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToParentElement]}
              >
                <SortableContext items={images.map(i => i.id)} strategy={rectSortingStrategy}>
                  <Grid container spacing={2} mt={2}>
                    {images.map((item, index) => (
                      <SortableThumb
                        key={item.id}
                        item={item}
                        index={index}
                        onRemove={() => deleteImage(item)}
                      />
                    ))}
                  </Grid>
                </SortableContext>
              </DndContext>
            )}
          </Box>

          {/* main fields */}
          <Grid container spacing={2}>
            {/* VIN */}<Grid item xs={12}><TextField label="VIN" fullWidth value={vin} disabled /></Grid>
            {/* Make */}<Grid item xs={12} sm={6}>
              <TextField select label="Make" disabled fullWidth value={make} onChange={e => setMake(e.target.value)}>
                {selectOptions.makes.map((option) => (
                  <MenuItem key={option.id} value={option.id || ""}>{option.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {/* Model */}<Grid item xs={12} sm={6}><TextField disabled select label="Model" fullWidth value={model} onChange={e => setModel(e.target.value)}>{selectOptions.models.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}</TextField></Grid>
            {/* Variant & Reg# */}<Grid item xs={12} sm={6}><TextField label="Variant" fullWidth name="variant" value={formValues.variant} onChange={handleChange} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Registration Number" fullWidth name="registrationNumber" value={formValues.registrationNumber} onChange={handleChange} /></Grid>
            {/* Date */}<Grid item xs={12} sm={6}><DatePicker label="First Registration Date" value={firstRegDate} onChange={setFirstRegDate} slotProps={{ textField: { fullWidth: true } }} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Km" fullWidth name="km" type="number" value={formValues.km} onChange={handleChange} /></Grid>
            {/* CarType & FuelType */}<Grid item xs={12} sm={6}><TextField select label="Car Type" fullWidth name="carType" value={carType} onChange={e => setCarType(e.target.value)}>{selectOptions.carTypes.map(opt => <MenuItem key={opt.key} value={opt.key}>{opt.display}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} sm={6}><TextField select label="Fuel Type" fullWidth name="fuelType" value={fuelType} onChange={e => setFuelType(e.target.value)}>{selectOptions.fuelTypes.map(opt => <MenuItem key={opt.key} value={opt.key}>{opt.display}</MenuItem>)}</TextField></Grid>
            {/* Color */} <Grid item xs={12} sm={6}><TextField label="Color" fullWidth name="color" value={formValues.color} onChange={handleChange} /></Grid>
            {/* Color */} <Grid item xs={12} sm={6}><TextField label="Price" fullWidth name="price" value={formValues.price} onChange={handleChange} /></Grid>
            <Grid item xs={12} sm={6}><TextField select label="Admin" fullWidth name="admin" value={adminId} onChange={e => setAdminId(e.target.value)}>{selectOptions.admins.map(opt => <MenuItem key={opt.id} value={opt.id}>{opt.firstName + " " + opt.lastName + " - (" + opt.email + ")"}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.vatReclaimable}
                    onChange={handleChange}
                    name="vatReclaimable"
                    color="primary"
                  />
                }
                label={`VAT Reclaimable - (${formValues.vatReclaimable ? 'Yes' : 'No'})`}
              />
            </Grid>
          </Grid>

          {/* Description */}
          <Box mt={2}><TextField label="Description" name="description" fullWidth multiline minRows={4} value={formValues.description} onChange={handleChange} /></Box>

          <Box display="flex" gap={4} mb={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.onHold}
                  onChange={handleChange}
                  name="onHold"
                  color="primary"
                />
              }
              label="On Hold"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.externalVisible}
                  onChange={handleChange}
                  name="externalVisible"
                  color="primary"
                />
              }
              label="Visible to Buyers"
            />
          </Box>

          {/* Technical Details */}
          <Box mt={2}><Button variant="text" startIcon={showTechFields ? <ExpandLessIcon /> : <ExpandMoreIcon />} onClick={() => setShowTechFields(prev => !prev)}>{showTechFields ? 'Hide technical details' : 'Show technical details'}</Button>
            <Collapse in={showTechFields}><Grid container spacing={2} mt={1}><Grid item xs={12} sm={4}><TextField label="Seats" fullWidth name="seats" type="number" value={formValues.seats} onChange={handleChange} /></Grid><Grid item xs={12} sm={4}><TextField label="Doors" fullWidth name="doors" type="number" value={formValues.doors} onChange={handleChange} /></Grid><Grid item xs={12} sm={4}><TextField label="HP" fullWidth name="hp" type="number" value={formValues.hp} onChange={handleChange} /></Grid></Grid></Collapse>
          </Box>

          {/* Internal Notes */}
          <Box mt={2}><Button variant="text" startIcon={showInternalNotes ? <ExpandLessIcon /> : <ExpandMoreIcon />} onClick={() => setShowInternalNotes(prev => !prev)}>{showInternalNotes ? 'Hide internal notes' : 'Show internal notes'}</Button>
            <Collapse in={showInternalNotes}><Box mt={2}><TextField label="Internal Notes" name="internalNotes" fullWidth multiline minRows={4} value={formValues.internalNotes} onChange={handleChange} /></Box></Collapse>
          </Box>

          {/* Save Button */}
          <Box mt={4} display="flex" justifyContent="flex-end"><Button variant="contained" color="primary" onClick={handleSaveListing}>Update Listing</Button></Box>
        </Paper>
        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}><MuiAlert severity={snackbar.severity}>{snackbar.message}</MuiAlert></Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default EditListingPage;
